import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Collapse, Box, Typography, Badge, useMediaQuery, ListItemButton, Alert, Button, Stack } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Header from './Header';
import menuItems from './menuItems';
import { FONT_FAMILY, LINK_COLOR, HOVER_BG, ACTIVE_BG, SIDEBAR_BG, SIDEBAR_BORDER } from '../Constant';
import LinearLoading from './reuse/LinearLoading';
import { useGetApiLoading } from '../hooks/useLoadingState';
import { useSelector, useDispatch } from 'react-redux';
import { dismissShopifyAlert } from '../redux/actions/uiActions';

function Layout({ children }) {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:900px)');
  const isGetApiLoading = useGetApiLoading();
  const dispatch = useDispatch();
  const { user, userpackageinfos } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.billing);
  const dismissedShopifyAlert = useSelector((state) => state.ui.dismissedShopifyAlert);
  const SHOPIFY_PRICING_URL = 'https://admin.shopify.com/store/dev-dl1961/apps/agentzena/app/pricing';
  
  // Keep Settings dropdown open if any settings subpage is active
  useEffect(() => {
    if ([
      '/ecommerce-setup',
      '/email-setup',
      '/settings',
      '/billing-usage',
      '/basic-setup',
      '/settings/packages',
    ].includes(location.pathname)) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const handleSettingsClick = () => setSettingsOpen((open) => !open);
  const handleDrawerToggle = () => setMobileOpen((open) => !open);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Company Name with Icon */}
      {user?.company && (
        <Box sx={{ 
          px: 2, 
          py: 2, 
          borderBottom: `1px solid ${SIDEBAR_BORDER}`,
          mb: 1 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5 
          }}>
            {/* Company Icon */}
            <Box sx={{
              width: 32,
              height: 32,
              backgroundColor: '#000',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Box sx={{
                width: 20,
                height: 20,
                border: '2px solid #fff',
                borderRadius: 0.5,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 12,
                  height: 4,
                  backgroundColor: '#fff',
                  borderRadius: '2px 2px 0 0'
                }
              }} />
            </Box>
            {/* Company Name */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: 14, 
                  fontWeight: 700, 
                  color: '#000',
                  fontFamily: FONT_FAMILY,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.2
                }}
              >
                Workspace
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: '#666',
                  fontFamily: FONT_FAMILY,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  lineHeight: 1.2
                }}
              >
                {user.company}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      <List sx={{ fontSize: 14, fontFamily: FONT_FAMILY }}>
        {menuItems.map((item, idx) =>
          item.section ? (
            <Typography key={item.section + idx} sx={{ fontSize: 12, color: '#888', fontWeight: 500, px: 2, pt: 2, pb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: FONT_FAMILY }}>
              {item.section}
            </Typography>
          ) : (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  fontSize: 14,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === item.path ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemIcon sx={{ color: LINK_COLOR, minWidth: 32 }}>{item.icon}</ListItemIcon>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="default" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18, background: ACTIVE_BG, color: '#222', fontWeight: 600 } }}>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                  </Badge>
                ) : (
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                )}
              </ListItemButton>
            </ListItem>
          )
        )}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ my: 1 }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleSettingsClick}
            sx={{
              borderRadius: 2,
              color: LINK_COLOR,
              fontFamily: FONT_FAMILY,
              background: [
                '/ecommerce-setup',
                '/settings',
                '/billing-usage',
                '/basic-setup',
              ].includes(location.pathname)
                ? ACTIVE_BG
                : 'transparent',
              '&:hover': {
                background: HOVER_BG,
              },
              '&, & .MuiListItemIcon-root': {
                color: LINK_COLOR,
              },
            }}
          >
            <ListItemIcon sx={{ color: LINK_COLOR, minWidth: 32 }}><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
            {settingsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === '/ecommerce-setup' ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                component={Link}
                to="/ecommerce-setup"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText primary="Ecommerce Setup" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === '/email-setup' ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                component={Link}
                to="/email-setup"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText primary="Email Setup" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </ListItemButton>
            </ListItem> */}
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === '/billing-usage' ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                component={Link}
                to="/billing-usage"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText primary="Billing and Usage" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === '/settings/packages' ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                component={Link}
                to="/settings/packages"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText primary="Payment Setup" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === '/basic-setup' ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                component={Link}
                to="/basic-setup"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText primary="Basic Setup" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: SIDEBAR_BG, fontFamily: FONT_FAMILY }}>
      {/* Global Linear Loading Bar for GET APIs */}
      <LinearLoading show={isGetApiLoading} />
      <Header onMenuClick={handleDrawerToggle} />
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: 220 }, flexShrink: { sm: 0 } }} aria-label="sidebar">
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: 220,
              boxSizing: 'border-box',
              background: SIDEBAR_BG,
              borderRight: `1px solid ${SIDEBAR_BORDER}`,
              pt: '84px',
              fontFamily: FONT_FAMILY,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: 220,
              boxSizing: 'border-box',
              background: SIDEBAR_BG,
              borderRight: `1px solid ${SIDEBAR_BORDER}`,
              pt: '84px',
              fontFamily: FONT_FAMILY,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      {/* Main content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 8, fontFamily: FONT_FAMILY, minWidth: 0, overflowX: 'hidden' }}>
        <Box sx={{ p: { xs: 1, sm: 4 }, width: '100%', minWidth: 0 }}>
          {
          loading ? null :
          !userpackageinfos && !dismissedShopifyAlert && (
            <Stack sx={{ width: '100%', mb: 2 }} spacing={2}>
              <Alert
                severity="warning"
                onClose={() => { dispatch(dismissShopifyAlert()); }}
              >
                You have not selected any package yet. Please select a package to continue.
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => { window.open(SHOPIFY_PRICING_URL, '_blank', 'noopener'); }}
                  sx={{ ml: 2, fontSize: '12px' }}
                >
                  Select Package
                </Button>
              </Alert>
            </Stack>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
